<template>
  <div style="display: inline-flex; align-items: center; gap: 12px;">
    <span style="font-size: 14px; font-style: italic; opacity: 0.8;">Download</span>
    <span 
      style="cursor: pointer; font-size: 14px; font-weight: 600; color: white; opacity: 0.9;" 
      @click="toggleFormat"
    >
      {{ format.toUpperCase() }}
    </span>
    <v-btn 
      :class="['btn-secondary', format === 'pdf' ? 'active-button' : '']" 
      style="padding: 0 16px; height: 32px; background-color: rgba(255, 255, 255, 0.2); color: white; border: 1px solid white;" 
      @click="exportData"
    >
      <v-icon size="20">mdi-download</v-icon>
    </v-btn>
  </div>
</template>

<script>
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';

export default {
name: 'ExportPayments',
props: {
  models: {
    type: Array,
    required: true,
    default: () => []
  },
  period: {
    type: String,
    default: 'All'
  },
  usdXe: {
    type: Number,
    default: 4000
  },
  eurXe: {
    type: Number,
    default: 4000
  }
},
data() {
  return {
    format: 'pdf',
    lastExported: 'N/A'
  };
},
methods: {
  toggleFormat() {
    this.format = this.format === 'pdf' ? 'csv' : 'pdf';
  },
  exportData() {
    if (this.format === 'pdf') {
      this.downloadPDF();
    } else {
      this.downloadCSV();
    }
    this.lastExported = new Date().toLocaleString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    this.$emit('exported', this.lastExported);
  },
  formatCurrency(value, currency, hideSymbol = false) {
    if (value === null || value === undefined || isNaN(value)) return '0.00';
    if (currency !== 'COP') {
      return new Intl.NumberFormat('en-US', {
        style: hideSymbol ? 'decimal' : 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
    return new Intl.NumberFormat('en-US', {
      style: hideSymbol ? 'decimal' : 'currency',
      currency: 'COP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  },
  displayCommission(commission) {
    if (commission === null || commission === undefined || isNaN(commission)) return '0%';
    return `${(commission * 100).toFixed(0)}%`;
  },
  hasAdjustments(model) {
    return model.adjustments && model.adjustments.length > 0;
  },
  hasDeductions(model) {
    return model.deductions && model.deductions.length > 0;
  },
  async downloadPDF() {
    console.log('Starting downloadPDF...', this.models);

    if (!this.models || this.models.length === 0) {
      console.log('No models to export. Aborting PDF generation.');
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const logoUrl = '/logo_mgroup.png';
    let logoImg;

    try {
      const response = await fetch(logoUrl);
      if (!response.ok) throw new Error('Failed to fetch logo');
      const blob = await response.blob();
      const reader = new FileReader();
      logoImg = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error loading logo image:', error);
      logoImg = null;
    }

    doc.bigText = () => doc.setFontSize(14);
    doc.normalText = () => doc.setFontSize(10);
    doc.smallText = () => doc.setFontSize(7);
    doc.tinyText = () => doc.setFontSize(6);

    const renderModelPDF = (doc, model, yOffsetStart) => {
      let yOffset = yOffsetStart;
      const colUnit = (pageWidth - 20) / 12;

      doc.setFont('helvetica', 'bold');
      doc.smallText();
      doc.text(model.fullName || 'N/A', 10, yOffset);
      yOffset += 4;

      doc.setFont('helvetica', 'normal');
      doc.text(model.stageName || 'N/A', 10, yOffset);
      doc.text(`TRM USD: ${(model.trmUSD || this.usdXe || 0).toLocaleString('en-US')}`, 10 + colUnit * 4, yOffset);
      doc.text(`Fecha: ${model.paymentDate || 'N/A'}`, 10 + colUnit * 7, yOffset);
      yOffset += 4;

      doc.smallText();
      doc.text(`${model.docType || 'Documento'}: ${model.docNumber || 'N/A'}`, 10, yOffset);
      doc.text(`TRM EUR: ${(model.trmEUR || this.eurXe || 0).toLocaleString('en-US')}`, 10 + colUnit * 4, yOffset);
      doc.text(`Periodo: ${model.period || 'N/A'}`, 10 + colUnit * 7, yOffset);

      const logoX = pageWidth - 40;
      const logoY = yOffsetStart - 4;
      if (logoImg) {
        const maxWidthPdf = 300 * 0.0353 * 2.83465;
        const maxHeightPdf = 140 * 0.0353 * 2.83465;
        doc.addImage(logoImg, 'PNG', logoX, logoY, maxWidthPdf, maxHeightPdf);
      }

      yOffset += 4;
      doc.setLineWidth(0.5);
      doc.line(10, yOffset, pageWidth - 10, yOffset);

      yOffset += 7;
      doc.setFontSize(12);
      doc.text('Ganancias', 10, yOffset);
      yOffset += 2;
      const earningsBody = (model.earnings || []).map(earn => [
        earn.platform || 'N/A',
        earn.period || 'N/A',
        earn.currency || 'N/A',
        (earn.earnings || 0).toLocaleString('en-US'),
        this.displayCommission(earn.commission || 0),
        (earn.copTotal || 0).toLocaleString('en-US')
      ]);
      autoTable(doc, {
        startY: yOffset,
        head: [['Plataforma', 'Periodo', 'Moneda', 'Ganancia', 'Comisión', 'Total COP']],
        body: earningsBody,
        styles: { fontSize: 10, textColor: [0, 0, 0] },
        headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 11, halign: 'left' },
        bodyStyles: { fillColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [255, 255, 255] }
      });
      yOffset = doc.lastAutoTable.finalY;

      if (this.hasAdjustments(model)) {
        yOffset += 6;
        doc.text('Ajustes', 10, yOffset);
        yOffset += 2;
        const adjBody = model.adjustments.map(adj => [
          adj.type || 'N/A',
          (adj.amount || 0).toLocaleString('en-US'),
          adj.currency || 'N/A',
          adj.description || 'N/A',
          adj.number || 'N/A',
          adj.quotas || 'N/A',
          adj.date || 'N/A',
          ((adj.copTotal || 0) * (adj.sign || 1)).toLocaleString('en-US')
        ]);
        autoTable(doc, {
          startY: yOffset,
          head: [['Tipo', 'Monto', 'Moneda', 'Descripción', '#', 'Cuotas', 'Fecha', 'Total COP']],
          body: adjBody,
          styles: { fontSize: 10, textColor: [0, 0, 0] },
          headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 11, halign: 'left' },
          bodyStyles: { fillColor: [255, 255, 255] },
          alternateRowStyles: { fillColor: [255, 255, 255] }
        });
        yOffset = doc.lastAutoTable.finalY;
      }

      if (this.hasDeductions(model)) {
        yOffset += 6;
        doc.text('Deducciones', 10, yOffset);
        yOffset += 2;
        const dedBody = model.deductions.map(ded => [
          ded.type || 'N/A',
          (ded.amount || 0).toLocaleString('en-US'),
          ded.currency || 'N/A',
          ded.description || 'N/A',
          ded.number || 'N/A',
          ded.quotas || 'N/A',
          ded.date || 'N/A',
          ((ded.copTotal || 0) * (ded.sign || 1)).toLocaleString('en-US')
        ]);
        autoTable(doc, {
          startY: yOffset,
          head: [['Tipo', 'Monto', 'Moneda', 'Descripción', '#', 'Cuotas', 'Fecha', 'Total COP']],
          body: dedBody,
          styles: { fontSize: 10, textColor: [0, 0, 0] },
          headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 11, halign: 'left' },
          bodyStyles: { fillColor: [255, 255, 255] },
          alternateRowStyles: { fillColor: [255, 255, 255] }
        });
        yOffset = doc.lastAutoTable.finalY;
      }

      yOffset += 4;
      doc.line(10, yOffset, pageWidth - 10, yOffset);
      yOffset += 2;

      const lineY = yOffset + 6.5;
      const colWidth = (pageWidth - 20) / 12;

      doc.line(10, lineY, 10 + colWidth * 3, lineY);
      doc.tinyText();
      doc.text('Firmar', 10, lineY + 3);

      const cedulaX = 10 + colWidth * 3 + 10;
      doc.line(cedulaX, lineY, cedulaX + colWidth * 3, lineY);
      doc.text('Cédula', cedulaX, lineY + 3);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      const totalCopX = pageWidth - 15;
      doc.text(`Ganancias Totales: ${this.formatCurrency(model.totalEarnings || 0, 'COP', true)} COP`, totalCopX, lineY, { align: 'right' });
    };

    this.models.forEach((model, index) => {
      if (index > 0) doc.addPage();
      renderModelPDF(doc, model, 10);
      if (doc.lastAutoTable && doc.lastAutoTable.finalY + 150 < pageHeight) {
        const cutGuideY = doc.lastAutoTable.finalY + 20;
        doc.setDrawColor(200);
        doc.setLineWidth(0.1);
        doc.setLineDash([1, 1], 0);
        doc.line(10, cutGuideY, pageWidth - 10, cutGuideY);
        doc.setLineDash([]);
        renderModelPDF(doc, model, doc.lastAutoTable.finalY + 26);
      } else {
        doc.addPage();
        renderModelPDF(doc, model, 10);
      }
    });

    const blob = doc.output('blob');
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl);
    console.log('PDF saved successfully.');
  },
  downloadCSV() {
    // Note: In Excel, apply dark grey background (#A9A9A9) and bold font to rows with headers:
    // - 'Nombre', 'Tipo', 'Plataforma', 'Periodo', 'Moneda', 'Ventas', 'Commission', 'Total COP'
    // - 'Tipo', 'Fecha', 'Description', 'Moneda', 'Numero', 'Quota', 'Total COP'
    // Apply bold font to rows with 'Total Ganancias' and its TotalCOP value.
    const csvData = [];

    this.models.forEach(model => {
      // Add main header (bold and dark grey in Excel)
      csvData.push({
        Nombre: 'Nombre',
        Tipo: 'Tipo',
        Plataforma: 'Plataforma',
        Periodo: 'Periodo',
        Moneda: 'Moneda',
        Ventas: 'Ventas',
        Commission: 'Commission',
        TotalCOP: 'Total COP'
      });

      // Add model details and earnings
      csvData.push({
        Nombre: model.fullName || 'N/A',
        Tipo: 'Ganancias',
        Plataforma: model.earnings[0]?.platform || 'N/A',
        Periodo: model.earnings[0]?.period || 'N/A',
        Moneda: model.earnings[0]?.currency || 'N/A',
        Ventas: model.earnings[0]?.earnings || 0,
        Commission: this.displayCommission(model.earnings[0]?.commission),
        TotalCOP: model.earnings[0]?.copTotal?.toFixed(2) || '0.00'
      });

      csvData.push({
        Nombre: model.stageName || 'N/A',
        Tipo: 'Ganancias',
        Plataforma: model.earnings[1]?.platform || 'N/A',
        Periodo: model.earnings[1]?.period || 'N/A',
        Moneda: model.earnings[1]?.currency || 'N/A',
        Ventas: model.earnings[1]?.earnings || 0,
        Commission: this.displayCommission(model.earnings[1]?.commission),
        TotalCOP: model.earnings[1]?.copTotal?.toFixed(2) || '0.00'
      });

      csvData.push({
        Nombre: `Cedula: ${model.docNumber || 'N/A'}`,
        Tipo: 'Ganancias',
        Plataforma: model.earnings[2]?.platform || 'N/A',
        Periodo: model.earnings[2]?.period || 'N/A',
        Moneda: model.earnings[2]?.currency || 'N/A',
        Ventas: model.earnings[2]?.earnings || 0,
        Commission: this.displayCommission(model.earnings[2]?.commission),
        TotalCOP: model.earnings[2]?.copTotal?.toFixed(2) || '0.00'
      });

      // Add remaining earnings
      model.earnings.slice(3).forEach(earn => {
        csvData.push({
          Nombre: '',
          Tipo: 'Ganancias',
          Plataforma: earn.platform || 'N/A',
          Periodo: earn.period || 'N/A',
          Moneda: earn.currency || 'N/A',
          Ventas: earn.earnings || 0,
          Commission: this.displayCommission(earn.commission),
          TotalCOP: earn.copTotal?.toFixed(2) || '0.00'
        });
      });

      // Add adjustments and deductions sub-header if applicable (bold and dark grey in Excel)
      if (this.hasAdjustments(model) || this.hasDeductions(model)) {
        csvData.push({
          Nombre: '',
          Tipo: 'Tipo',
          Plataforma: 'Fecha',
          Periodo: 'Description',
          Moneda: 'Moneda',
          Ventas: 'Numero',
          Commission: 'Quota',
          TotalCOP: 'Total COP'
        });
      }

      // Add adjustments
      model.adjustments.forEach(adj => {
        csvData.push({
          Nombre: '',
          Tipo: 'Ajuste',
          Plataforma: adj.date || 'N/A',
          Periodo: adj.description || 'N/A',
          Moneda: adj.currency || 'N/A',
          Ventas: adj.number || 'N/A',
          Commission: adj.quotas || 'N/A',
          TotalCOP: ((adj.copTotal || 0) * (adj.sign || 1)).toFixed(2)
        });
      });

      // Add deductions
      model.deductions.forEach(ded => {
        csvData.push({
          Nombre: '',
          Tipo: 'Descuento',
          Plataforma: ded.date || 'N/A',
          Periodo: ded.description || 'N/A',
          Moneda: ded.currency || 'N/A',
          Ventas: ded.number || 'N/A',
          Commission: ded.quotas || 'N/A',
          TotalCOP: ((ded.copTotal || 0) * (ded.sign || 1)).toFixed(2)
        });
      });

      // Add total earnings (bold in Excel)
      csvData.push({
        Nombre: '',
        Tipo: 'Total Ganancias',
        Plataforma: '',
        Periodo: '',
        Moneda: '',
        Ventas: '',
        Commission: '',
        TotalCOP: (model.totalEarnings || 0).toFixed(2)
      });

      // Add blank row for separation
      csvData.push({});
    });

    const csv = Papa.unparse(csvData, {
      columns: ['Nombre', 'Tipo', 'Plataforma', 'Periodo', 'Moneda', 'Ventas', 'Commission', 'TotalCOP']
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `Payments_${this.period}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
};
</script>